/* eslint-disable require-jsdoc */

import PropTypes from 'prop-types';
import React from 'react';
import Switch from 'react-router-dom/Switch';
import Route from 'react-router-dom/Route';
import Redirect from 'react-router-dom/Redirect';
import Loadable from 'react-loadable';
import AppLoadingContainer from 'containers/AppLoading/AppLoadingContainer';
import NotFound from 'components/NotFound/NotFound';
import PageLoading from 'components/PageLoading/PageLoading';

PrivateRoute.propTypes = {
    component: PropTypes.func.isRequired,
    isAuthed: PropTypes.bool.isRequired
};

function PrivateRoute({ component: Component, isAuthed, ...rest }) {
    return (
        <Route {...rest} render={(routeProps) => (
            isAuthed
                ? <Component {...routeProps} />
                : <Redirect to={{
                    pathname: '/login',
                    state: { from: routeProps.location }
                }}/>
        )}/>
    );
}

function PageLoadable(opts) {
    return Loadable(Object.assign({
        loading: PageLoading,
    }, opts));
}

const AsyncHome = PageLoadable({ loader: () => import('containers/Home/HomeContainer') });
const AsyncLogin = PageLoadable({ loader: () => import('containers/Login/LoginContainer') });
const AsyncPLA = PageLoadable({ loader: () => import('containers/PLA/PLAContainer') });
const AsyncPLAList = PageLoadable({ loader: () => import('containers/PLA/Admin/PLAListContainer') });
const AsyncPLAEditor = PageLoadable({ loader: () => import('containers/PLA/Admin/PLAEditorContainer') });
const AsyncSLA = PageLoadable({ loader: () => import('containers/SLA/SLAContainer') });
const AsyncSLAList = PageLoadable({ loader: () => import('containers/SLA/Admin/SLAListContainer') });
const AsyncSLAEditor = PageLoadable({ loader: () => import('containers/SLA/Admin/SLAEditorContainer') });
const AsyncSurveyList = PageLoadable({ loader: () => import('containers/Survey/Admin/SurveyListContainer') });
const AsyncSurveyEditor = PageLoadable({ loader: () => import('containers/Survey/Admin/SurveyEditorContainer') });
const AsyncSurvey = PageLoadable({ loader: () => import('containers/Survey/SurveyContainer') });
const AsyncSurveySubmit = PageLoadable({ loader: () => import('containers/Survey/SurveySubmitContainer') });
const AsyncDocuments = PageLoadable({ loader: () => import('containers/Documents/DocumentsContainer') });
const AsyncContacts = PageLoadable({ loader: () => import('containers/Contacts/ContactsContainer') });
const AsyncJournalsList = PageLoadable({ loader: () => import('containers/Journals/JournalsListContainer') });
const AsyncJournalPage = PageLoadable({ loader: () => import('containers/Journals/JournalPageContainer') });
const AsyncSettings = PageLoadable({ loader: () => import('containers/Settings/SettingsContainer') });
const AsyncGroupList = PageLoadable({ loader: () => import('containers/Group/GroupListContainer') });
const AsyncGroupCreate = PageLoadable({ loader: () => import('containers/Group/GroupCreateContainer') });
const AsyncReminders = PageLoadable({ loader: () => import('containers/Reminders/RemindersContainer') });
const AsyncOnboard = PageLoadable({ loader: () => import('containers/Onboard/OnboardContainer') });
const AsyncGroupDash = PageLoadable({ loader: () => import('containers/Group/GroupDashboardContainer') });
const AsyncGroupUserDetails = PageLoadable({ loader: () => import('containers/Group/GroupUserDetailsContainer') });
const AsyncAssignments = PageLoadable({ loader: () => import('containers/Assignments/AssignmentsContainer') });
const AsyncAssignmentsDashboard = PageLoadable({ loader: () => import('containers/Assignments/AssignmentsDashboardContainer') });
const AsyncSystemEmail = PageLoadable({ loader: () => import('containers/SystemEmail/Admin/SystemEmailContainer') });
const AsyncAssessment = PageLoadable({ loader: () => import('containers/Assessment/AssessmentContainer') });
const AsyncAssessmentEditor = PageLoadable({ loader: () => import('containers/Assessment/AssessmentEditorContainer') });
const AsyncTextTemplates = PageLoadable({ loader: () => import('containers/TextTemplates/TextTemplatesContainer') });
const AsyncCoachesList = PageLoadable({ loader: () => import('containers/Coaches/CoachesListContainer') });
const AsyncCoachCreate = PageLoadable({ loader: () => import('containers/Coaches/CoachCreateContainer') });

export default function getRoutes(isAuthed, isPublic) {
    if (isPublic) {
        return (
            <AppLoadingContainer>
                <Switch>
                    <Route path='/onboard' exact={true} component={AsyncOnboard} />
                    <Route path='/' exact={true} component={AsyncHome} />
                </Switch>
            </AppLoadingContainer>
        );
    }
    else {
        return (
            <AppLoadingContainer>
                <Switch>
                    <Route path='/login' exact={true} component={AsyncLogin} />
                    <PrivateRoute path='/profile/edit' exact={true} component={AsyncSettings} isAuthed={isAuthed}/>
                    <PrivateRoute path='/journals' exact={true} component={AsyncJournalsList} isAuthed={isAuthed} />
                    <PrivateRoute path='/journals/:journal_id' exact={true} component={AsyncJournalPage} isAuthed={isAuthed} />
                    <PrivateRoute path='/contacts' exact={true} component={AsyncContacts} isAuthed={isAuthed} />
                    <PrivateRoute path='/group/list' exact={true} component={AsyncGroupList} isAuthed={isAuthed} />
                    <PrivateRoute path='/group/create' exact={true} component={AsyncGroupCreate} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/ongoing' exact={true} component={AsyncPLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/initial' exact={true} component={AsyncPLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/archive' exact={true} component={AsyncPLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/list' exact={true} component={AsyncPLAList} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/create' exact={true} component={AsyncPLAEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/pla/edit/:id' exact={true} component={AsyncPLAEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/ongoing' exact={true} component={AsyncSLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/initial' exact={true} component={AsyncSLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/archive' exact={true} component={AsyncSLA} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/list' exact={true} component={AsyncSLAList} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/create' exact={true} component={AsyncSLAEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/sla/edit/:id' exact={true} component={AsyncSLAEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/survey/list' exact={true} component={AsyncSurveyList} isAuthed={isAuthed} />
                    <PrivateRoute path='/survey/create' exact={true} component={AsyncSurveyEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/survey/edit/:id' exact={true} component={AsyncSurveyEditor} isAuthed={isAuthed} />
                    <PrivateRoute path='/survey' exact={true} component={AsyncSurvey} isAuthed={isAuthed} />
                    <Route path='/survey/submit/:token' exact={true} component={AsyncSurveySubmit} />
                    <PrivateRoute path='/documents/diagnostics' exact={true} component={AsyncDocuments} isAuthed={isAuthed} />
                    <PrivateRoute path='/documents/resources' exact={true} component={AsyncDocuments} isAuthed={isAuthed} />
                    <PrivateRoute path='/documents/visuals' exact={true} component={AsyncDocuments} isAuthed={isAuthed} />
                    <PrivateRoute path='/reminders' exact={true} component={AsyncReminders} isAuthed={isAuthed} />
                    <PrivateRoute path='/group/:id/dashboard/users' exact={true} component={AsyncGroupDash} isAuthed={isAuthed}/>
                    <PrivateRoute path='/group/:id/dashboard/messages' exact={true} component={AsyncGroupDash} isAuthed={isAuthed}/>
                    <PrivateRoute path='/group/:id/dashboard/admin' exact={true} component={AsyncGroupDash} isAuthed={isAuthed}/>
                    <PrivateRoute path='/group/:id/dashboard/executive' exact={true} component={AsyncGroupDash} isAuthed={isAuthed}/>
                    <PrivateRoute path='/group/:id/dashboard/reports' exact={true} component={AsyncGroupDash} isAuthed={isAuthed}/>
                    <PrivateRoute path='/group/:id/users' exact={true} component={AsyncGroupUserDetails} isAuthed={isAuthed}/>

                    <PrivateRoute path='/assignments/dashboard' exact={true} component={AsyncAssignmentsDashboard} isAuthed={isAuthed}/>

                    <PrivateRoute path='/assignments/feedbacks' exact={true} component={AsyncAssignments} isAuthed={isAuthed}/>

                    <PrivateRoute path='/assignments/upload' exact={true} component={AsyncAssignments} isAuthed={isAuthed}/>
                    <PrivateRoute path='/assignments/completed' exact={true} component={AsyncAssignments} isAuthed={isAuthed}/>
                    <PrivateRoute path='/assignments/ongoing' exact={true} component={AsyncAssignments} isAuthed={isAuthed}/>

                    <PrivateRoute path='/assessment/dashboard' exact={true} component={AsyncAssessment} isAuthed={isAuthed}/>
                    <PrivateRoute path='/assessment/create' exact={true} component={AsyncAssessmentEditor} isAuthed={isAuthed}/>
                    <PrivateRoute path='/assessment/edit/:id/:isEditable' exact={true} component={AsyncAssessmentEditor} isAuthed={isAuthed}/>

                    <PrivateRoute path='/se/list' exact={true} component={AsyncSystemEmail} isAuthed={isAuthed}/>

                    <PrivateRoute path='/templates' exact={true} component={AsyncTextTemplates} isAuthed={isAuthed}/>

                    <PrivateRoute path='/coaches/list' exact={true} component={AsyncCoachesList} isAuthed={isAuthed}/>
                    <PrivateRoute path='/coaches/create' exact={true} component={AsyncCoachCreate} isAuthed={isAuthed}/>

                    <Route component={NotFound} />
                </Switch>
            </AppLoadingContainer>
        );
    }
}



// WEBPACK FOOTER //
// ./src/config/routes.js